import { createContext, ReactElement, useCallback, useMemo } from 'react';

import { rem, StyleProps, Text } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { useBonusTextTokens } from '../../components/bonus/hooks/use-bonus-text-tokens';
import BonusTextSegment, { DecorateSegment } from './bonus-text-segment';
import { Token, TokenType } from './lib/lexer';

export interface BonusTextProps extends StyleProps {
	bonus: Bonus;
	decorateSegment?: DecorateSegment;
	checkForAmount?: boolean;
	prefix?: ReactElement;
	text: string;
}

export const BonusTextContext = createContext<Bonus | null>(null);

export const BonusText: React.FC<BonusTextProps> = ({ bonus, decorateSegment, checkForAmount, prefix, text, ...rest }: BonusTextProps) => {
	const tokens = useBonusTextTokens(text, checkForAmount);
	const company = useCurrentCompany();
	const shouldDisplayToken = useCallback(
		(token: Token) => token.kind !== TokenType.AMOUNT || (token.kind === TokenType.AMOUNT && token.text !== '+0' && company?.show_bonus_amounts),
		[company?.show_bonus_amounts]
	);
	const displayTokens = useMemo(() => tokens.filter(shouldDisplayToken), [shouldDisplayToken, tokens]);

	if (!company) return null;
	return (
		<BonusTextContext.Provider value={bonus}>
			<Text as="div" fontWeight="normal" size="lg" whiteSpace="pre-line" data-behavior="bonus-text" {...rest}>
				{prefix && (
					<Text as="span" fontWeight="semibold" size="lg" py={rem(16)}>
						{prefix}{' '}
					</Text>
				)}

				{displayTokens.map((token, i) => (
					<BonusTextSegment decorateSegment={decorateSegment} key={i} token={token} />
				))}
			</Text>
		</BonusTextContext.Provider>
	);
};
